import {
  IHouseStepHouseLocation,
  IWizardHouseState,
  TStepErrors,
  TStepPayload,
  TWizardStepComponentEditable,
  TWizardStepComponentEditableView,
  WIZARD_HOUSE_STEPS_PATHS
} from "../../../interfaces";
import { Form, Input, Select, theme } from "antd";
import { StepViewOnlyValue } from "../../../Student/styles";
import React, {
  forwardRef,
  useImperativeHandle,
  useMemo,
  useState
} from "react";
import { useTranslation } from "react-i18next";
import {
  createCheckIsReadonlyStepField,
  getChangedFields,
  getStepErrorsFromResponse
} from "../../../utils";

import { HouseAPI } from "../../../../../API/houseApi";
import { getCountries } from "../../../../../utils/fetchMapInfo";
import { TLang } from "../../../../../assets/data/isoNameLanguage";
import { ALLOWED_HOUSE_COUNTRIES } from "../../../../../constants";
import styled from "styled-components/macro";
import { useNotifier } from "../../../../../context/ContextNotifier";

const statePath = WIZARD_HOUSE_STEPS_PATHS.HouseLocation;
export const WizardStepHouseLocation: TWizardStepComponentEditable<IWizardHouseState> =
  forwardRef(({ state, readonly, viewonly }, ref) => {
    const [values, setValues] = useState<TStepPayload<IHouseStepHouseLocation>>(
      state[statePath] ?? {}
    );
    const [errors, setErrors] = useState<TStepErrors<typeof values>>(null);
    const notifier = useNotifier();
    const { t: terror } = useTranslation("errors");
    useImperativeHandle(ref, () => ({
      save: () => {
        setErrors(null);
        return HouseAPI.wizardSaveHouseLocation(
          state.house_id!,
          state.wizard_type!,
          values
        )
          .then((result) => {
            return {
              ...state,
              [statePath]: result
            };
          })
          .catch((e) => {
            setErrors(getStepErrorsFromResponse(e));
            let errMsg: string;
            errMsg = e?.response?.data?.message;
            if (
              e?.response?.data?.message?.includes("Could not unpack address")
            ) {
              errMsg = terror("errors.E1");
            }
            if (e?.response?.data?.message) {
              notifier.error({
                message: errMsg
              });
            }
            return Promise.reject(e);
          });
      },
      savePartially: () => {
        setErrors(null);
        return HouseAPI.wizardSaveHouseLocationPartially(
          state.house_id!,
          state.wizard_type!,
          getChangedFields(state[statePath], values)
        )
          .then((result) => {
            return {
              ...state,
              [statePath]: result
            };
          })
          .catch((e) => {
            setErrors(getStepErrorsFromResponse(e));
            return Promise.reject(e);
          });
      }
    }));

    return (
      <WizardStepHouseLocationView
        values={values}
        setValues={setValues}
        errors={errors}
        readonly={readonly}
        viewonly={viewonly}
      />
    );
  });

const Root = styled(Form)`
  display: grid;
  grid-template-columns: auto auto 210px;
  gap: 24px;

  @media ${(props) => props.theme.media.tablet} {
    grid-template-columns: 1fr 1fr;
  }

  @media ${({ theme }) => theme.media.phone} {
    grid-template-columns: 1fr;
  }
`;

export const WizardStepHouseLocationView: TWizardStepComponentEditableView<
  IHouseStepHouseLocation,
  { commonError?: boolean }
> = React.memo(
  ({
    errors,
    commonError,
    values,
    setValues,
    viewonly,
    readonly,
    enabledFields
  }) => {
    const { t, i18n } = useTranslation("house");
    const designTokes = theme.useToken();

    const countries = useMemo(
      () =>
        getCountries(i18n.language.toUpperCase() as TLang)
          .filter((o) => ALLOWED_HOUSE_COUNTRIES.includes(o.value.toString()))
          .sort((a, b) => {
            if (a.value === "NL") {
              return -1;
            }

            return a.label.toLowerCase() < b.label.toLowerCase() ? -1 : 1;
          }),
      [i18n.language]
    );
    const inputSize = "large";

    const isReadonly = createCheckIsReadonlyStepField(readonly, enabledFields);

    return (
      <>
        {commonError ? (
          <div
            className="ant-form-item-has-error"
            style={{
              color: designTokes.token.colorError
            }}
          >
            {t("wizard.errors.address_not_found")}
          </div>
        ) : null}
        <Root layout="vertical">
          <Form.Item
            label={t(`wizard.steps.HouseLocation.form.country.title`)}
            validateStatus={errors?.country ? "error" : ""}
            help={errors?.country ? t("wizard.errors.generic") : ""}
          >
            {viewonly ? (
              <StepViewOnlyValue>
                {countries.find((c) => c.value === values.country)?.label ?? ""}
              </StepViewOnlyValue>
            ) : (
              <Select
                size={inputSize}
                disabled={isReadonly("country")}
                value={values.country ?? undefined}
                options={countries}
                showSearch={true}
                placeholder={t(
                  `wizard.steps.HouseLocation.form.country.placeholder`
                )}
                filterOption={(input, option) =>
                  (option?.label ?? "")
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                onChange={(val) => {
                  setValues({
                    ...values,
                    country: val
                  });
                }}
              />
            )}
          </Form.Item>
          <Form.Item
            label={t(`wizard.steps.HouseLocation.form.city.title`)}
            validateStatus={errors?.city ? "error" : ""}
            help={errors?.city ? t("wizard.errors.generic") : ""}
          >
            {viewonly ? (
              <StepViewOnlyValue>{values.city ?? ""}</StepViewOnlyValue>
            ) : (
              <Input
                size={inputSize}
                disabled={isReadonly("city")}
                value={values.city ?? undefined}
                placeholder={t(
                  `wizard.steps.HouseLocation.form.city.placeholder`
                )}
                onChange={(e) => {
                  setValues({
                    ...values,
                    city: e.target.value
                  });
                }}
              />
            )}
          </Form.Item>
          <Form.Item
            label={t(`wizard.steps.HouseLocation.form.postal_code.title`)}
            validateStatus={errors?.postal_code ? "error" : ""}
            help={errors?.postal_code ? t("wizard.errors.generic") : ""}
          >
            {viewonly ? (
              <StepViewOnlyValue>{values.postal_code ?? ""}</StepViewOnlyValue>
            ) : (
              <Input
                size={inputSize}
                disabled={isReadonly("postal_code")}
                value={values.postal_code ?? undefined}
                placeholder={t(
                  `wizard.steps.HouseLocation.form.postal_code.placeholder`
                )}
                onChange={(e) => {
                  setValues({
                    ...values,
                    postal_code: e.target.value
                  });
                }}
              />
            )}
          </Form.Item>
          <Form.Item
            label={t(`wizard.steps.HouseLocation.form.street.title`)}
            validateStatus={errors?.street ? "error" : ""}
            help={errors?.street ? t("wizard.errors.generic") : ""}
          >
            {viewonly ? (
              <StepViewOnlyValue>{values.street ?? ""}</StepViewOnlyValue>
            ) : (
              <Input
                size={inputSize}
                disabled={isReadonly("street")}
                value={values.street ?? undefined}
                placeholder={t(
                  `wizard.steps.HouseLocation.form.street.placeholder`
                )}
                onChange={(e) => {
                  setValues({
                    ...values,
                    street: e.target.value
                  });
                }}
              />
            )}
          </Form.Item>
          <Form.Item
            label={t(`wizard.steps.HouseLocation.form.house_number.title`)}
            validateStatus={errors?.house_number ? "error" : ""}
            help={errors?.house_number ? t("wizard.errors.generic") : ""}
          >
            {viewonly ? (
              <StepViewOnlyValue>{values.house_number ?? ""}</StepViewOnlyValue>
            ) : (
              <Input
                size={inputSize}
                disabled={isReadonly("house_number")}
                value={values.house_number ?? undefined}
                placeholder={t(
                  `wizard.steps.HouseLocation.form.house_number.placeholder`
                )}
                onChange={(e) => {
                  setValues({
                    ...values,
                    house_number: e.target.value
                  });
                }}
              />
            )}
          </Form.Item>
          <Form.Item
            label={t(
              `wizard.steps.HouseLocation.form.additional_house_number.title`
            )}
            validateStatus={errors?.additional_house_number ? "error" : ""}
            help={
              errors?.additional_house_number ? t("wizard.errors.generic") : ""
            }
          >
            {viewonly ? (
              <StepViewOnlyValue>
                {values.additional_house_number ?? ""}
              </StepViewOnlyValue>
            ) : (
              <Input
                size={inputSize}
                disabled={isReadonly("additional_house_number")}
                value={values.additional_house_number ?? undefined}
                placeholder={t(
                  `wizard.steps.HouseLocation.form.house_number.placeholder`
                )}
                onChange={(e) => {
                  setValues({
                    ...values,
                    additional_house_number: e.target.value
                  });
                }}
              />
            )}
          </Form.Item>
        </Root>
      </>
    );
  }
);
